// aiAttached — the STRUCTURED attachments the user hands the assistant
// (VaultOS v2 design §4.1).
//
// One root bag, `root.aiAttached = { refs: EntityRef[], canvas: <payload>|null }`,
// declared in state-app.js beside `pickedElements` and wiped on a workspace
// switch for the same disclosure reason (a ref names a tenant row).
//
// WHY ROOT AND NOT THE COMPOSER BAG. The composer's own draft state
// (`draft/files/refs/images`, serialised by buildPromptPayload) survives only
// as TEXT in the prompt body — `sent: 'text-only'`. These attachments are
// different in kind: they ride the wire as `context.attached`, the server
// keeps them after its allowlist + caps, and the orchestrator renders them as
// a fenced ATTACHED CONTEXT block and registers every ref as a retrieval
// candidate. That is `sent: 'structured'`, and it must be reachable from a
// PAGE (`el.call('attachAiRef', ref)`) as well as from the drawer composer, so
// it cannot live on one composer's element state.
//
// THE ATTACHMENTS SURVIVE A TURN. Sending does NOT clear them (unlike
// `pickedElements`, which sendPrompt snapshots onto the message and empties).
// The user attached a data room / a canvas / a contract to talk about it for
// several questions; clearing it under them would make every follow-up
// silently ungrounded. `clearAiAttached` and the per-chip remove are the only
// two ways out, and both are explicit user acts.
//
// Every function here is `this`-bound and reached via el.call — same shape as
// removePickedElement / removePromptAttachment (shared/promptAttachments.js).

// Max refs on one turn. Mirrors the server allowlist cap (spec §4.1, ≤24) so
// the client never renders a chip for something the wire will drop — the
// awareness contract is "the UI cannot claim more than the wire carries".
export const AI_ATTACHED_REF_CAP = 24

// Pure reader — the ONE normalizer for the bag's shape. Tolerates every state
// a live root can be in: undeclared, `{}` (what the workspace-switch wipe
// leaves behind — `_freshEmpty` returns a bare object for an object default),
// or a fully-populated bag. Never returns null, so callers have no null branch.
export const readAiAttached = (root) => {
  const bag =
    root && typeof root.aiAttached === 'object' && root.aiAttached ? root.aiAttached : null
  return {
    refs: bag && Array.isArray(bag.refs) ? bag.refs : [],
    canvas: bag && bag.canvas && typeof bag.canvas === 'object' ? bag.canvas : null
  }
}

// attachAiRef(ref) — add ONE entity ref to the turn's structured context.
// `ref` is anything parseEntityRef accepts (the string form, the object form,
// or a drag-ref whose `kind` is the EntityRef `type`). Idempotent: attaching
// the same entity twice keeps ONE chip and refreshes its title/meta, because a
// second drag of the same card is a re-attach, not a duplicate.
// Returns true when the bag changed.
export const attachAiRef = function (ref, opts) {
  const el = this
  const root =
    (el && el.state && el.state.root) || (el && el.getRoot && el.getRoot()?.state) || null
  if (!root || typeof root.update !== 'function') return false
  // A drag-ref carries `kind` where an EntityRef carries `type` — accept both
  // at this seam so a drop handler can hand the payload straight through.
  const raw =
    ref && typeof ref === 'object' && !ref.type && ref.kind ? { ...ref, type: ref.kind } : ref
  let parsed = null
  try {
    parsed = el.call('parseEntityRef', raw)
  } catch (_) {
    parsed = null
  }
  if (!parsed) return false
  const key = el.call('formatEntityRef', parsed)
  if (!key) return false
  const bag =
    root && typeof root.aiAttached === 'object' && root.aiAttached ? root.aiAttached : null
  const current = bag && Array.isArray(bag.refs) ? bag.refs : []
  const canvas = bag && bag.canvas && typeof bag.canvas === 'object' ? bag.canvas : null
  // Re-attach REPLACES in place (keeps chip order stable) instead of appending.
  let replaced = false
  const next = current.map((r) => {
    if (replaced) return r
    let existing = ''
    try {
      existing = el.call('formatEntityRef', r)
    } catch (_) {
      existing = ''
    }
    if (existing !== key) return r
    replaced = true
    return parsed
  })
  if (!replaced) {
    // 24 = AI_ATTACHED_REF_CAP, inlined. FA204 / frank trap: a module-scope
    // const read from inside an exported handler is `undefined` once this file
    // is frank.toJSON'd, and `next.length >= undefined` is always false — the
    // cap would silently stop existing in a published consumer. The exported
    // constant above stays live for callers that need to SHOW the cap.
    if (next.length >= 24) return false
    next.push(parsed)
  }
  root.update({ aiAttached: { refs: next, canvas } })
  // ── REVEAL: attaching SHOWS the assistant (2026-09-09, P0) ─────────────
  // "Attach this to the assistant" is a user act whose whole point is that
  // the assistant now has the thing. Writing `root.aiAttached` alone made it
  // a silent no-op from any surface where the drawer was closed: nothing
  // moved, no chip appeared, no error — measured on the deployed staging
  // shell from a module list row. Every other attach site in this repo
  // already pairs the write with the open patch by hand (calendar
  // EventDetailModal, tickets TicketDetailModal, canvas), so the behaviour
  // was never in doubt; only its home was. It belongs HERE, on the shell's
  // own attach API, where no caller can forget it and no caller has to
  // learn the shell's state keys.
  //
  // The patch is the deep-link opener's, verbatim (app.js `aiConversation`):
  // a MINIMIZED dock has to be restored or `assistantOpen` alone shows
  // nothing. `{ reveal: false }` opts out for a programmatic attach that
  // must not steal the screen.
  const reveal = !(opts && opts.reveal === false)
  if (reveal) {
    const patch = { assistantOpen: true }
    if (root.assistantDock === 'minimized') patch.assistantDock = 'right'
    root.update(patch)
  }
  return true
}

// detachAiRef(ref) — drop ONE ref by identity. Accepts the same inputs as
// attachAiRef (the chip's removeVia descriptor passes the string form).
// Returns true when something was removed.
export const detachAiRef = function (ref) {
  const el = this
  const root =
    (el && el.state && el.state.root) || (el && el.getRoot && el.getRoot()?.state) || null
  if (!root || typeof root.update !== 'function') return false
  const raw =
    ref && typeof ref === 'object' && !ref.type && ref.kind ? { ...ref, type: ref.kind } : ref
  let key = ''
  try {
    key = el.call('formatEntityRef', raw)
  } catch (_) {
    key = ''
  }
  if (!key) return false
  const bag =
    root && typeof root.aiAttached === 'object' && root.aiAttached ? root.aiAttached : null
  const current = bag && Array.isArray(bag.refs) ? bag.refs : []
  const canvas = bag && bag.canvas && typeof bag.canvas === 'object' ? bag.canvas : null
  const next = current.filter((r) => {
    let existing = ''
    try {
      existing = el.call('formatEntityRef', r)
    } catch (_) {
      existing = ''
    }
    return existing !== key
  })
  if (next.length === current.length) return false
  root.update({ aiAttached: { refs: next, canvas } })
  return true
}

// attachAiCanvas(payload) — attach ONE canvas board (spec §3.8 shape:
// `{ projectId, boardPageId, title, nodes: [], connections: [] }`). A second
// call REPLACES the first — a turn carries at most one board, which is what
// the server allowlist accepts. `null` detaches, so a surface needs no second
// function to take it back.
export const attachAiCanvas = function (payload, opts) {
  const el = this
  const root =
    (el && el.state && el.state.root) || (el && el.getRoot && el.getRoot()?.state) || null
  if (!root || typeof root.update !== 'function') return false
  const bag =
    root && typeof root.aiAttached === 'object' && root.aiAttached ? root.aiAttached : null
  const refs = bag && Array.isArray(bag.refs) ? bag.refs : []
  const canvas = payload && typeof payload === 'object' ? payload : null
  root.update({ aiAttached: { refs, canvas } })
  // Same reveal rule as attachAiRef — see the note there. `null` DETACHES,
  // and taking something back must never open a panel.
  const reveal = !!canvas && !(opts && opts.reveal === false)
  if (reveal) {
    const patch = { assistantOpen: true }
    if (root.assistantDock === 'minimized') patch.assistantDock = 'right'
    root.update(patch)
  }
  return true
}

// clearAiAttached() — the "start clean" act. Empties both halves.
export const clearAiAttached = function () {
  const el = this
  const root =
    (el && el.state && el.state.root) || (el && el.getRoot && el.getRoot()?.state) || null
  if (!root || typeof root.update !== 'function') return false
  root.update({ aiAttached: { refs: [], canvas: null } })
  return true
}
